export const adminV2ToolFieldControls = [
  'text',
  'number',
  'slider',
  'select',
  'multi-select',
  'toggle',
  'image',
  'image_file',
  'video_file',
  'audio_file',
  'file',
  'url',
  'array',
] as const;

export type AdminV2ToolFieldControl = (typeof adminV2ToolFieldControls)[number];

export type AdminV2ToolFieldInputRole =
  | 'parameter'
  | 'primaryImage'
  | 'referenceImage'
  | 'firstFrame'
  | 'lastFrame'
  | 'referenceVideo'
  | 'referenceAudio'
  | 'prompt'
  | 'internal';

export type AdminV2WorkspaceParamType =
  | 'text'
  | 'url'
  | 'select'
  | 'multi-select'
  | 'array'
  | 'switch'
  | 'slider'
  | 'number'
  | 'prompt';

export type AdminV2ToolModelParameterType = AdminV2ModelParameterType;

type ItemConstraintMode = 'never' | 'always' | 'batchUpload';
export type AdminV2ToolFieldDefaultValueKind =
  | 'none'
  | 'string'
  | 'number'
  | 'boolean'
  | 'option'
  | 'optionArray'
  | 'array';

export type AdminV2ToolFieldControlCapability = {
  control: AdminV2ToolFieldControl;
  workspaceParamType: AdminV2WorkspaceParamType | null;
  extensionSupported: boolean;
  allowsLabel: boolean;
  allowsPlaceholder: boolean;
  allowsInfoText: boolean;
  defaultValueKind: AdminV2ToolFieldDefaultValueKind;
  allowsOptions: boolean;
  allowsNumericConstraints: boolean;
  allowsLengthConstraints: boolean;
  itemConstraints: ItemConstraintMode;
  allowsBatchUpload: boolean;
  allowsImageConstraints: boolean;
  allowsMediaConstraints: boolean;
  allowsClientImageMetaRequirements: boolean;
  allowsAccessPolicy: boolean;
  allowsRequired: boolean;
  consumedByWorkspaceParamList: boolean;
  consumedByWorkspaceUploadArea: boolean;
  inputRoles: readonly AdminV2ToolFieldInputRole[];
  modelParameterTypes: readonly AdminV2ToolModelParameterType[];
};

export const adminV2ToolFieldControlCapabilities = {
  text: {
    control: 'text',
    workspaceParamType: 'text',
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: true,
    allowsInfoText: true,
    defaultValueKind: 'string',
    allowsOptions: false,
    allowsNumericConstraints: false,
    allowsLengthConstraints: true,
    itemConstraints: 'never',
    allowsBatchUpload: false,
    allowsImageConstraints: false,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: true,
    consumedByWorkspaceUploadArea: false,
    inputRoles: ['parameter', 'prompt', 'internal'],
    modelParameterTypes: ['string'],
  },
  number: {
    control: 'number',
    workspaceParamType: 'number',
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: true,
    allowsInfoText: true,
    defaultValueKind: 'number',
    allowsOptions: false,
    allowsNumericConstraints: true,
    allowsLengthConstraints: false,
    itemConstraints: 'never',
    allowsBatchUpload: false,
    allowsImageConstraints: false,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: true,
    consumedByWorkspaceUploadArea: false,
    inputRoles: ['parameter', 'internal'],
    modelParameterTypes: ['number'],
  },
  slider: {
    control: 'slider',
    workspaceParamType: 'slider',
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: false,
    allowsInfoText: true,
    defaultValueKind: 'number',
    allowsOptions: false,
    allowsNumericConstraints: true,
    allowsLengthConstraints: false,
    itemConstraints: 'never',
    allowsBatchUpload: false,
    allowsImageConstraints: false,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: true,
    consumedByWorkspaceUploadArea: false,
    inputRoles: ['parameter', 'internal'],
    modelParameterTypes: ['number'],
  },
  select: {
    control: 'select',
    workspaceParamType: 'select',
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: false,
    allowsInfoText: true,
    defaultValueKind: 'option',
    allowsOptions: true,
    allowsNumericConstraints: false,
    allowsLengthConstraints: false,
    itemConstraints: 'never',
    allowsBatchUpload: false,
    allowsImageConstraints: false,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: true,
    consumedByWorkspaceUploadArea: false,
    inputRoles: ['parameter', 'internal'],
    modelParameterTypes: ['string', 'number', 'boolean', 'enum'],
  },
  'multi-select': {
    control: 'multi-select',
    workspaceParamType: 'multi-select',
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: false,
    allowsInfoText: true,
    defaultValueKind: 'optionArray',
    allowsOptions: true,
    allowsNumericConstraints: false,
    allowsLengthConstraints: false,
    itemConstraints: 'always',
    allowsBatchUpload: false,
    allowsImageConstraints: false,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: true,
    consumedByWorkspaceUploadArea: false,
    inputRoles: ['parameter', 'internal'],
    modelParameterTypes: ['string_array'],
  },
  toggle: {
    control: 'toggle',
    workspaceParamType: 'switch',
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: false,
    allowsInfoText: true,
    defaultValueKind: 'boolean',
    allowsOptions: false,
    allowsNumericConstraints: false,
    allowsLengthConstraints: false,
    itemConstraints: 'never',
    allowsBatchUpload: false,
    allowsImageConstraints: false,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: true,
    consumedByWorkspaceUploadArea: false,
    inputRoles: ['parameter', 'internal'],
    modelParameterTypes: ['boolean'],
  },
  image: {
    control: 'image',
    workspaceParamType: null,
    extensionSupported: false,
    allowsLabel: true,
    allowsPlaceholder: false,
    allowsInfoText: true,
    defaultValueKind: 'none',
    allowsOptions: false,
    allowsNumericConstraints: false,
    allowsLengthConstraints: false,
    itemConstraints: 'never',
    allowsBatchUpload: false,
    allowsImageConstraints: true,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: true,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: false,
    consumedByWorkspaceUploadArea: false,
    inputRoles: ['parameter', 'internal'],
    modelParameterTypes: ['string'],
  },
  image_file: {
    control: 'image_file',
    workspaceParamType: null,
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: false,
    allowsInfoText: true,
    defaultValueKind: 'none',
    allowsOptions: false,
    allowsNumericConstraints: false,
    allowsLengthConstraints: false,
    itemConstraints: 'batchUpload',
    allowsBatchUpload: true,
    allowsImageConstraints: true,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: true,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: false,
    consumedByWorkspaceUploadArea: true,
    inputRoles: [
      'parameter',
      'primaryImage',
      'referenceImage',
      'firstFrame',
      'lastFrame',
      'internal',
    ],
    modelParameterTypes: ['string', 'string_array'],
  },
  video_file: {
    control: 'video_file',
    workspaceParamType: null,
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: false,
    allowsInfoText: true,
    defaultValueKind: 'none',
    allowsOptions: false,
    allowsNumericConstraints: false,
    allowsLengthConstraints: false,
    itemConstraints: 'batchUpload',
    allowsBatchUpload: true,
    allowsImageConstraints: false,
    allowsMediaConstraints: true,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: false,
    consumedByWorkspaceUploadArea: true,
    inputRoles: ['parameter', 'referenceVideo', 'internal'],
    modelParameterTypes: ['string', 'string_array'],
  },
  audio_file: {
    control: 'audio_file',
    workspaceParamType: null,
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: false,
    allowsInfoText: true,
    defaultValueKind: 'none',
    allowsOptions: false,
    allowsNumericConstraints: false,
    allowsLengthConstraints: false,
    itemConstraints: 'batchUpload',
    allowsBatchUpload: true,
    allowsImageConstraints: false,
    allowsMediaConstraints: true,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: false,
    consumedByWorkspaceUploadArea: true,
    inputRoles: ['parameter', 'referenceAudio', 'internal'],
    modelParameterTypes: ['string', 'string_array'],
  },
  file: {
    control: 'file',
    workspaceParamType: null,
    extensionSupported: false,
    allowsLabel: true,
    allowsPlaceholder: false,
    allowsInfoText: true,
    defaultValueKind: 'none',
    allowsOptions: false,
    allowsNumericConstraints: false,
    allowsLengthConstraints: false,
    itemConstraints: 'never',
    allowsBatchUpload: false,
    allowsImageConstraints: false,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: false,
    consumedByWorkspaceUploadArea: false,
    inputRoles: ['parameter', 'internal'],
    modelParameterTypes: ['string'],
  },
  url: {
    control: 'url',
    workspaceParamType: 'url',
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: true,
    allowsInfoText: true,
    defaultValueKind: 'string',
    allowsOptions: false,
    allowsNumericConstraints: false,
    allowsLengthConstraints: true,
    itemConstraints: 'never',
    allowsBatchUpload: false,
    allowsImageConstraints: false,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: true,
    consumedByWorkspaceUploadArea: false,
    inputRoles: ['parameter', 'internal'],
    modelParameterTypes: ['string'],
  },
  array: {
    control: 'array',
    workspaceParamType: 'array',
    extensionSupported: true,
    allowsLabel: true,
    allowsPlaceholder: true,
    allowsInfoText: true,
    defaultValueKind: 'array',
    allowsOptions: false,
    allowsNumericConstraints: false,
    allowsLengthConstraints: false,
    itemConstraints: 'always',
    allowsBatchUpload: false,
    allowsImageConstraints: false,
    allowsMediaConstraints: false,
    allowsClientImageMetaRequirements: false,
    allowsAccessPolicy: true,
    allowsRequired: true,
    consumedByWorkspaceParamList: true,
    consumedByWorkspaceUploadArea: false,
    inputRoles: ['parameter', 'internal'],
    modelParameterTypes: ['string_array'],
  },
} as const satisfies Record<
  AdminV2ToolFieldControl,
  AdminV2ToolFieldControlCapability
>;

export function getAdminV2ToolFieldControlCapability(
  control: AdminV2ToolFieldControl
): AdminV2ToolFieldControlCapability {
  return adminV2ToolFieldControlCapabilities[control];
}

export function isAdminV2ToolFieldControl(
  control: string
): control is AdminV2ToolFieldControl {
  return adminV2ToolFieldControls.includes(control as AdminV2ToolFieldControl);
}

export function supportsAdminV2ToolFieldDefaultValue(
  control: AdminV2ToolFieldControl
) {
  return (
    getAdminV2ToolFieldControlCapability(control).defaultValueKind !== 'none'
  );
}

export function getAdminV2ToolFieldDefaultValueKind(
  control: AdminV2ToolFieldControl
) {
  return getAdminV2ToolFieldControlCapability(control).defaultValueKind;
}

export function supportsAdminV2ToolFieldPlaceholder(params: {
  control: AdminV2ToolFieldControl;
  inputRole?: AdminV2ToolFieldInputRole;
}) {
  if (params.control === 'text' && params.inputRole === 'prompt') {
    return false;
  }
  return getAdminV2ToolFieldControlCapability(params.control).allowsPlaceholder;
}

export function supportsAdminV2ToolFieldOptions(
  control: AdminV2ToolFieldControl
) {
  return getAdminV2ToolFieldControlCapability(control).allowsOptions;
}

export function supportsAdminV2ToolFieldItemConstraints(params: {
  control: AdminV2ToolFieldControl;
  allowBatch?: boolean;
}) {
  const mode = getAdminV2ToolFieldControlCapability(
    params.control
  ).itemConstraints;
  if (mode === 'always') {
    return true;
  }
  if (mode === 'batchUpload') {
    return params.allowBatch === true;
  }
  return false;
}

export function resolveAdminV2WorkspaceParamType(params: {
  control: AdminV2ToolFieldControl;
  inputRole?: AdminV2ToolFieldInputRole;
}) {
  if (params.control === 'text' && params.inputRole === 'prompt') {
    return 'prompt';
  }
  return getAdminV2ToolFieldControlCapability(params.control)
    .workspaceParamType;
}

export function getAdminV2ToolFieldControlsForModelParameterType(
  type: AdminV2ToolModelParameterType
) {
  return adminV2ToolFieldControls.filter((control) =>
    getAdminV2ToolFieldControlCapability(control).modelParameterTypes.includes(
      type
    )
  );
}

export function isAdminV2ToolFieldControlCompatibleWithModelParameterType(params: {
  control: AdminV2ToolFieldControl;
  type: AdminV2ToolModelParameterType;
  allowBatch?: boolean;
}) {
  if (
    ['image_file', 'video_file', 'audio_file'].includes(params.control) &&
    params.type === 'string_array'
  ) {
    return params.allowBatch === true;
  }
  return getAdminV2ToolFieldControlCapability(
    params.control
  ).modelParameterTypes.includes(params.type);
}
import type { AdminV2ModelParameterType } from '@/admin-v2/models/model-parameter-registry';
